import { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import JsBarcode from 'jsbarcode';

function GestionCodesBarres() {
  const [lecteurs, setLecteurs] = useState([]);
  const [produits, setProduits] = useState([]);
  const [operations, setOperations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showAssociationModal, setShowAssociationModal] = useState(false);
  const [editingLecteur, setEditingLecteur] = useState(null);
  const [selectedLecteur, setSelectedLecteur] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    identifiant: '',
    statut: 'actif',
    mode_affichage: 'classique'
  });
  const [selectedOperations, setSelectedOperations] = useState([]);
  const [filterProduit, setFilterProduit] = useState('');

  useEffect(() => {
    loadLecteurs();
    loadProduits();
    loadOperations();
  }, []);

  const loadLecteurs = async () => {
    const { data, error } = await supabase
      .from('lecteurs_codes_barres')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Erreur chargement lecteurs:', error);
    } else {
      setLecteurs(data || []);
    }
  };

  const loadProduits = async () => {
    const { data, error } = await supabase
      .from('types_produits')
      .select('*')
      .order('nom', { ascending: true });
    
    if (error) {
      console.error('Erreur chargement produits:', error);
    } else {
      setProduits(data || []);
    }
  };

  const loadOperations = async () => {
    const { data, error } = await supabase
      .from('operations')
      .select('*, types_produits(nom)')
      .order('ordre', { ascending: true });
    
    if (error) {
      console.error('Erreur chargement opérations:', error);
    } else {
      setOperations(data || []);
    }
  };

  const loadOperationsForLecteur = async (lecteurId) => {
    const { data, error } = await supabase
      .from('lecteurs_operations')
      .select('operation_id')
      .eq('lecteur_id', lecteurId);
    
    if (error) {
      console.error('Erreur chargement associations:', error);
      return [];
    }
    return data.map(item => item.operation_id);
  };

  const generateIdentifiant = () => {
    // Alphabet compatible QWERTY/AZERTY (sans A, M, Q, W, Z qui changent entre les deux layouts)
    // Également sans O, I pour éviter confusion avec 0, 1
    const chars = '0123456789BCDEFGHJKLNPRSTUVXY';
    let code = 'LECT';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (editingLecteur) {
      const { error } = await supabase
        .from('lecteurs_codes_barres')
        .update(formData)
        .eq('id', editingLecteur.id);
      
      if (!error) {
        loadLecteurs();
        closeModal();
      } else {
        console.error('Erreur mise à jour:', error);
        alert('Erreur lors de la mise à jour');
      }
    } else {
      const identifiant = formData.identifiant || generateIdentifiant();
      const dataToInsert = { ...formData, identifiant };
      
      const { error } = await supabase
        .from('lecteurs_codes_barres')
        .insert([dataToInsert]);
      
      if (!error) {
        loadLecteurs();
        closeModal();
      } else {
        console.error('Erreur création:', error);
        alert('Erreur lors de la création');
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Supprimer ce lecteur ? Les associations avec les opérations seront également supprimées.')) {
      const { error } = await supabase
        .from('lecteurs_codes_barres')
        .delete()
        .eq('id', id);
      
      if (!error) {
        loadLecteurs();
      } else {
        console.error('Erreur suppression:', error);
        alert('Erreur lors de la suppression');
      }
    }
  };

  const openModal = (lecteur = null) => {
    if (lecteur) {
      setEditingLecteur(lecteur);
      setFormData({
        nom: lecteur.nom,
        description: lecteur.description || '',
        identifiant: lecteur.identifiant,
        statut: lecteur.statut,
        mode_affichage: lecteur.mode_affichage || 'classique'
      });
    } else {
      setEditingLecteur(null);
      setFormData({
        nom: '',
        description: '',
        identifiant: '',
        statut: 'actif',
        mode_affichage: 'classique'
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingLecteur(null);
    setFormData({
      nom: '',
      description: '',
      identifiant: '',
      statut: 'actif',
      mode_affichage: 'classique'
    });
  };

  const openAssociationModal = async (lecteur) => {
    setSelectedLecteur(lecteur);
    const operationIds = await loadOperationsForLecteur(lecteur.id);
    setSelectedOperations(operationIds);
    setShowAssociationModal(true);
  };

  const closeAssociationModal = () => {
    setShowAssociationModal(false);
    setSelectedLecteur(null);
    setSelectedOperations([]);
    setFilterProduit('');
  };

  const handleOperationToggle = (operationId) => {
    setSelectedOperations(prev => {
      if (prev.includes(operationId)) {
        return prev.filter(id => id !== operationId);
      } else {
        return [...prev, operationId];
      }
    });
  };

  const handleSaveAssociations = async () => {
    if (!selectedLecteur) return;

    // Supprimer toutes les associations existantes
    await supabase
      .from('lecteurs_operations')
      .delete()
      .eq('lecteur_id', selectedLecteur.id);

    // Créer les nouvelles associations
    if (selectedOperations.length > 0) {
      const associations = selectedOperations.map(operationId => ({
        lecteur_id: selectedLecteur.id,
        operation_id: operationId
      }));

      const { error } = await supabase
        .from('lecteurs_operations')
        .insert(associations);

      if (error) {
        console.error('Erreur sauvegarde associations:', error);
        alert('Erreur lors de la sauvegarde des associations');
        return;
      }
    }

    closeAssociationModal();
    loadLecteurs();
  };

  const handleOpenScan = (lecteur) => {
    const url = `/poste-scan?lecteur=${lecteur.identifiant}`;
    window.open(url, '_blank');
  };

  const handlePrintBarcode = (lecteur) => {
    const canvas = document.createElement('canvas');
    
    try {
      JsBarcode(canvas, lecteur.identifiant, {
        format: 'CODE128',
        width: 3,
        height: 70,
        displayValue: false,
        margin: 0
      });
      
      const barcodeDataUrl = canvas.toDataURL('image/png');
      
      const printWindow = window.open('', '', 'width=400,height=200');
      const content = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Impression - ${lecteur.nom}</title>
            <style>
              @page {
                size: 89mm 36mm;
                margin: 0;
              }
              
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              
              body {
                font-family: Arial, sans-serif;
                width: 89mm;
                height: 36mm;
                margin: 0;
                padding: 2mm 2mm;
                overflow: hidden;
              }
              
              .label-container {
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
              }
              
              .barcode-section {
                text-align: center;
                margin-bottom: 0.5mm;
              }
              
              .barcode-image {
                width: 70mm;
                height: auto;
                max-height: 14mm;
                margin: 0 auto 0.5mm auto;
                display: block;
              }
              
              .code-text {
                font-family: 'Courier New', monospace;
                font-size: 8pt;
                font-weight: bold;
                color: #000;
                letter-spacing: 0.5px;
              }
              
              .product-info {
                text-align: center;
              }
              
              .name {
                font-size: 10pt;
                font-weight: bold;
                color: #000;
                margin-bottom: 0.5mm;
              }
              
              @media print {
                body {
                  width: 89mm;
                  height: 36mm;
                  padding: 2mm 2mm;
                }
              }
            </style>
          </head>
          <body>
            <div class="label-container">
              <div class="barcode-section">
                <img src="${barcodeDataUrl}" class="barcode-image" alt="Barcode"/>
                <div class="code-text">${lecteur.identifiant}</div>
              </div>
              
              <div class="product-info">
                <div class="name">${lecteur.nom}</div>
              </div>
            </div>
            
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  window.onafterprint = function() {
                    window.close();
                  };
                }, 250);
              };
            </script>
          </body>
        </html>
      `;
      
      printWindow.document.write(content);
      printWindow.document.close();
    } catch (error) {
      console.error('Erreur génération code-barre:', error);
      alert('Erreur lors de la génération du code-barre');
    }
  };

  const imprimerEtiquetteExitKiosk = () => {
    const canvas1 = document.createElement('canvas');
    const canvas2 = document.createElement('canvas');
    try {
      // Générer le premier code-barre EXITKIOSK
      JsBarcode(canvas1, 'EXITKIOSK', {
        format: 'CODE128',
        width: 3,
        height: 70,
        displayValue: false,
        margin: 0
      });
      
      // Générer le second code-barre avec les caractères déformés pour correspondre au mapping du scanner
      // Le scanner transformera "sudo shutdozn 6h noz" en "sudo shutdown -h now"
      JsBarcode(canvas2, 'sudo shutdozn 6h noz', {
        format: 'CODE128',
        width: 3,
        height: 70,
        displayValue: false,
        margin: 0
      });
      
      const barcode1DataUrl = canvas1.toDataURL('image/png');
      const barcode2DataUrl = canvas2.toDataURL('image/png');
      
      const printWindow = window.open('', '', 'width=400,height=400');
      const content = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Impression - Sortie Kiosk</title>
            <style>
              @page {
                size: 89mm 36mm;
                margin: 0;
              }
              
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              
              body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 0;
              }
              
              .page-break {
                page-break-after: always;
              }
              
              .label {
                width: 89mm;
                height: 36mm;
                padding: 2mm 2mm;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                gap: 2mm;
              }
              
              .barcode-section {
                text-align: center;
              }
              
              .barcode-image {
                width: 60mm;
                height: auto;
              }
              
              .code-text {
                font-family: 'Courier New', monospace;
                font-size: 8pt;
                font-weight: bold;
                color: #000;
                letter-spacing: 0.5px;
              }
              
              .label-title {
                font-size: 10pt;
                font-weight: bold;
                color: #000;
                text-align: center;
              }
              
              .step-number {
                font-size: 12pt;
                font-weight: bold;
                color: #ef4444;
                margin-bottom: 2mm;
              }
              
              @media print {
                body {
                  margin: 0;
                  padding: 0;
                }
              }
            </style>
          </head>
          <body>
            <!-- Étiquette 1 : EXITKIOSK -->
            <div class="label page-break">
              <div class="step-number">Étape 1</div>
              <div class="barcode-section">
                <img src="${barcode1DataUrl}" class="barcode-image" alt="Exit Kiosk"/>
                <div class="code-text">EXITKIOSK</div>
              </div>
              <div class="label-title">Sortie du mode Kiosk</div>
            </div>
            
            <!-- Étiquette 2 : sudo shutdown -h now (encodé avec mapping) -->
            <div class="label">
              <div class="step-number">Étape 2</div>
              <div class="barcode-section">
                <img src="${barcode2DataUrl}" class="barcode-image" alt="Shutdown"/>
                <div class="code-text">sudo shutdozn 6h noz</div>
              </div>
              <div class="label-title">Extinction du Raspberry Pi</div>
            </div>
            
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  window.onafterprint = function() {
                    window.close();
                  };
                }, 250);
              };
            </script>
          </body>
        </html>
      `;
      
      printWindow.document.write(content);
      printWindow.document.close();
    } catch (error) {
      console.error('Erreur génération code-barre:', error);
      alert('Erreur lors de la génération du code-barre');
    }
  };

  const filteredOperations = filterProduit 
    ? operations.filter(op => op.type_produit_id === filterProduit)
    : operations;

  const getStatutColor = (statut) => {
    switch(statut) {
      case 'actif': return '#10b981';
      case 'inactif': return '#6c757d';
      case 'maintenance': return '#f59e0b';
      default: return '#6c757d';
    }
  };

  const getStatutLabel = (statut) => {
    switch(statut) {
      case 'actif': return 'Actif';
      case 'inactif': return 'Inactif';
      case 'maintenance': return 'Maintenance';
      default: return statut;
    }
  };

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '30px'
      }}>
        <div>
          <h1 style={{ 
            color: '#212529',
            fontSize: '32px',
            fontWeight: '600',
            marginBottom: '10px'
          }}>
            Gestion des Lecteurs de Codes-Barres
          </h1>
          <p style={{ color: '#6c757d', fontSize: '15px' }}>
            Gérez vos lecteurs et leurs associations avec les opérations de production
          </p>
        </div>
        <div style={{ display: 'flex', gap: '15px' }}>
          <button
            onClick={() => openModal()}
            style={{
              padding: '12px 24px',
              backgroundColor: '#1aabf9',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              boxShadow: '0 2px 8px rgba(26, 171, 249, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
          >
            + Nouveau Lecteur
          </button>
          
          <button
            onClick={imprimerEtiquetteExitKiosk}
            style={{
              padding: '12px 24px',
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
          >
            🖨️ Imprimer "Sortie Kiosk"
          </button>
        </div>
      </div>

      {/* Liste des lecteurs */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
        gap: '20px'
      }}>
        {lecteurs.map(lecteur => (
          <div key={lecteur.id} style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '15px'
            }}>
              <div style={{ flex: 1 }}>
                <h3 style={{ 
                  color: '#212529',
                  fontSize: '18px',
                  fontWeight: '600',
                  marginBottom: '5px'
                }}>
                  {lecteur.nom}
                </h3>
                <div style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  backgroundColor: getStatutColor(lecteur.statut),
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {getStatutLabel(lecteur.statut)}
                </div>
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <div style={{ 
                color: '#6c757d', 
                fontSize: '13px',
                marginBottom: '8px'
              }}>
                <strong>Identifiant:</strong> {lecteur.identifiant}
              </div>
              {lecteur.description && (
                <div style={{ 
                  color: '#6c757d', 
                  fontSize: '13px',
                  marginBottom: '8px'
                }}>
                  {lecteur.description}
                </div>
              )}
              {lecteur.derniere_utilisation && (
                <div style={{ 
                  color: '#6c757d', 
                  fontSize: '12px',
                  fontStyle: 'italic'
                }}>
                  Dernière utilisation: {new Date(lecteur.derniere_utilisation).toLocaleString('fr-FR')}
                </div>
              )}
            </div>

            <div style={{
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => handleOpenScan(lecteur)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#e8f5e9',
                  color: '#2e7d32',
                  border: '1px solid #c8e6c9',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#c8e6c9';
                  e.target.style.borderColor = '#4caf50';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#e8f5e9';
                  e.target.style.borderColor = '#c8e6c9';
                }}
              >
                🖥️ Scanner
              </button>
              <button
                onClick={() => openAssociationModal(lecteur)}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  backgroundColor: '#f8f9fa',
                  color: '#1aabf9',
                  border: '1px solid #e0e5ea',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#e3f2fd';
                  e.target.style.borderColor = '#1aabf9';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                  e.target.style.borderColor = '#e0e5ea';
                }}
              >
                🔗 Opérations
              </button>
              <button
                onClick={() => handlePrintBarcode(lecteur)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#f8f9fa',
                  color: '#212529',
                  border: '1px solid #e0e5ea',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#e9ecef';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                }}
              >
                🖨️
              </button>
              <button
                onClick={() => openModal(lecteur)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#f8f9fa',
                  color: '#212529',
                  border: '1px solid #e0e5ea',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#e9ecef';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                }}
              >
                ✏️
              </button>
              <button
                onClick={() => handleDelete(lecteur.id)}
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#fff5f5',
                  color: '#dc3545',
                  border: '1px solid #fecaca',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#fee';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#fff5f5';
                }}
              >
                🗑️
              </button>
            </div>
          </div>
        ))}
      </div>

      {lecteurs.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          color: '#6c757d'
        }}>
          <p style={{ fontSize: '18px', marginBottom: '10px' }}>
            Aucun lecteur de codes-barres
          </p>
          <p style={{ fontSize: '14px' }}>
            Cliquez sur "Nouveau Lecteur" pour en ajouter un
          </p>
        </div>
      )}

      {/* Modal Création/Édition */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ 
              color: '#212529',
              fontSize: '24px',
              fontWeight: '600',
              marginBottom: '20px'
            }}>
              {editingLecteur ? 'Modifier le lecteur' : 'Nouveau lecteur'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  color: '#495057',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '8px'
                }}>
                  Nom du lecteur *
                </label>
                <input
                  type="text"
                  required
                  value={formData.nom}
                  onChange={(e) => setFormData({...formData, nom: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                  placeholder="Ex: Lecteur Poste 1"
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  color: '#495057',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '8px'
                }}>
                  Identifiant
                </label>
                <input
                  type="text"
                  value={formData.identifiant}
                  onChange={(e) => setFormData({...formData, identifiant: e.target.value})}
                  disabled={editingLecteur}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: editingLecteur ? '#f8f9fa' : 'white'
                  }}
                  placeholder="Laissez vide pour générer automatiquement"
                />
                <small style={{ color: '#6c757d', fontSize: '12px' }}>
                  {editingLecteur ? 'L\'identifiant ne peut pas être modifié' : 'Généré automatiquement si vide'}
                </small>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  color: '#495057',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '8px'
                }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    fontSize: '14px',
                    minHeight: '80px',
                    resize: 'vertical'
                  }}
                  placeholder="Emplacement du lecteur (ex: Atelier Zone A, Poste 1)"
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  color: '#495057',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '8px'
                }}>
                  Statut *
                </label>
                <select
                  required
                  value={formData.statut}
                  onChange={(e) => setFormData({...formData, statut: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="actif">Actif</option>
                  <option value="inactif">Inactif</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>

              {/* Mode d'affichage */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  color: '#495057',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '8px'
                }}>
                  Mode d'affichage *
                </label>
                <select
                  required
                  value={formData.mode_affichage}
                  onChange={(e) => setFormData({...formData, mode_affichage: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                >
                  <option value="classique">Écran classique (PC/Tablette)</option>
                  <option value="raspberry">Raspberry Pi (écran 3.5")</option>
                </select>
                <div style={{
                  marginTop: '6px',
                  fontSize: '12px',
                  color: '#6c757d',
                  fontStyle: 'italic'
                }}>
                  {formData.mode_affichage === 'raspberry' 
                    ? '📱 Interface simplifiée pour petit écran tactile'
                    : '🖥️ Interface complète avec toutes les informations'}
                </div>
              </div>

              <div style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={closeModal}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f8f9fa',
                    color: '#495057',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#1aabf9',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {editingLecteur ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Association Opérations */}
      {showAssociationModal && selectedLecteur && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2 style={{ 
              color: '#212529',
              fontSize: '24px',
              fontWeight: '600',
              marginBottom: '10px'
            }}>
              Associer des opérations
            </h2>
            <p style={{ 
              color: '#6c757d', 
              fontSize: '14px',
              marginBottom: '20px'
            }}>
              {selectedLecteur.nom}
            </p>

            {/* Filtre par produit */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                color: '#495057',
                fontSize: '14px',
                fontWeight: '500',
                marginBottom: '8px'
              }}>
                Filtrer par produit
              </label>
              <select
                value={filterProduit}
                onChange={(e) => setFilterProduit(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              >
                <option value="">Tous les produits</option>
                {produits.map(produit => (
                  <option key={produit.id} value={produit.id}>
                    {produit.nom}
                  </option>
                ))}
              </select>
            </div>

            {/* Liste des opérations */}
            <div style={{
              maxHeight: '400px',
              overflowY: 'auto',
              border: '1px solid #dee2e6',
              borderRadius: '6px',
              padding: '10px'
            }}>
              {filteredOperations.length === 0 ? (
                <p style={{ 
                  textAlign: 'center',
                  color: '#6c757d',
                  padding: '20px'
                }}>
                  Aucune opération disponible
                </p>
              ) : (
                filteredOperations.map(operation => (
                  <label
                    key={operation.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px',
                      backgroundColor: selectedOperations.includes(operation.id) ? '#e3f2fd' : 'transparent',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      marginBottom: '8px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (!selectedOperations.includes(operation.id)) {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!selectedOperations.includes(operation.id)) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedOperations.includes(operation.id)}
                      onChange={() => handleOperationToggle(operation.id)}
                      style={{
                        marginRight: '12px',
                        cursor: 'pointer',
                        width: '18px',
                        height: '18px'
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        color: '#212529',
                        fontWeight: '500',
                        fontSize: '14px',
                        marginBottom: '2px'
                      }}>
                        {operation.nom}
                      </div>
                      <div style={{
                        color: '#6c757d',
                        fontSize: '12px'
                      }}>
                        {operation.types_produits?.nom} - Ordre: {operation.ordre}
                        {operation.code_barre && ` - ${operation.code_barre}`}
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>

            <div style={{
              marginTop: '20px',
              padding: '12px',
              backgroundColor: '#f8f9fa',
              borderRadius: '6px',
              fontSize: '13px',
              color: '#495057'
            }}>
              <strong>{selectedOperations.length}</strong> opération(s) sélectionnée(s)
            </div>

            <div style={{
              display: 'flex',
              gap: '10px',
              justifyContent: 'flex-end',
              marginTop: '20px'
            }}>
              <button
                onClick={closeAssociationModal}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f8f9fa',
                  color: '#495057',
                  border: '1px solid #dee2e6',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleSaveAssociations}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#1aabf9',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GestionCodesBarres;
